import React, { useState, useEffect } from "react";
import { MoveLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";
import { getVehicleViewApi, updateStatusApi, deleteVehicleApi, getFuelEntryApi, getKMEntryApi } from "./vehicle-api";
import truckIcon from "../../assets/svg/vehicle_ic.svg";
import Dropdown from "react-bootstrap/Dropdown";
import { ReactComponent as DownArrowIc } from "../../assets/svg/down_arrow_ics.svg";
import { useGlobal } from "../../GlobalContext";
import { ReactComponent as ThreeDotIC } from "../../assets/svg/three-dot-ic.svg";
import FuelEntryModal from "./FuelEntryModal";
import KMEntryModal from "./KMEntryModal";
import DeleteModal from "../common/deleteModel";
import CommonSnackbar, { CommonPagination } from "../common/reusable-commponent";
import { sanitizeInput, sanitizeNumericInput } from "../common/security-utils";
import "../common/skeleton-effect.css";

export const VehicleView = () => {
  const { rolePermissionList, userType } = useGlobal();

  const location = useLocation();
  const navigate = useNavigate();
  const { id } = location.state || {};

  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ show: false, message: "", status: false });

  const triggerSnackbar = (message, status) => {
    setSnackbar({ show: true, message: sanitizeInput(message), status });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, show: false }));
  };

  const [vehicle, setVehicle] = useState({});
  const [images, setImages] = useState([]);
  const [selectedTab, setSelectedTab] = useState("kilometer");
  const [showFuelModal, setShowFuelModal] = useState(false);
  const [showKMModal, setShowKMModal] = useState(false);
  const [statusListOption, setListStageOption] = useState([
    { value: true, label: "Active" },
    { value: false, label: "In-Active" },
  ]);

  function checkPermission(permission) {
    return rolePermissionList.findIndex((element) => element["name"] === "Vehicle" && element["menu_permission"] === true && element[permission] === true) > -1 ? true : false;
  }

  useEffect(() => {
    if (id != null) {
      fetchVehicle();
    }
  }, [id]);

  useEffect(() => {
    if (id != null) {
      if (selectedTab === "kilometer") {
        getKMEntry();
      } else if (selectedTab === "fuel") {
        getFuelEntry();
      }
    }
  }, [id, selectedTab]);

  const fetchVehicle = async () => {
    try {
      setIsLoading(true);
      const res = await getVehicleViewApi(id);
      if (res?.data?.success === true) {
        const v = res.data.vehicle ?? {};
        const sanitizedVehicle = {
          ...v,
          vin: sanitizeInput(String(v.vin || "")),
          license_plate: sanitizeInput(String(v.license_plate || "")),
          vehicle_name: sanitizeInput(String(v.vehicle_name || "")),
          group_location: sanitizeInput(String(v.group_location || "")),
          vehicle_km: sanitizeInput(String(v.vehicle_km || "")),
          vehicle_color: sanitizeInput(String(v.vehicle_color || "")),
          body_type: sanitizeInput(String(v.body_type || "")),
          dimension: sanitizeInput(String(v.dimension || "")),
          length: sanitizeInput(String(v.length || "")),
          width: sanitizeInput(String(v.width || "")),
          height: sanitizeInput(String(v.height || "")),
          type_label: sanitizeInput(String(v.type_label || "")),
        };
        setVehicle(sanitizedVehicle);
        const rawImgs = v.Attachments ?? v.vehicle_images ?? [];
        const sanitizedImgs = rawImgs.map((it) => {
          if (typeof it === "string") return { file_path: sanitizeInput(it) };
          return { ...it, file_path: sanitizeInput(it.file_path || "") };
        });
        setImages(sanitizedImgs);
      }
    } catch {
      // Error handled by service/interceptor
    } finally {
      setIsLoading(false);
    }
  };
  const [lastKM, setLastKM] = useState("0");
  const [kilometerList, setKilometerList] = useState([]);
  const [fuelList, setFuelList] = useState([]);
  const [isKMLoading, setIsKMLoading] = useState(false);
  const [isFuelLoading, setIsFuelLoading] = useState(false);

  // Pagination state for Kilometer table
  const [kmItemsPerPage, setKmItemsPerPage] = useState(10);
  const [kmCurrentPage, setKmCurrentPage] = useState(0);
  const [kmTotalCount, setKmTotalCount] = useState(0);

  // Pagination state for Fuel table
  const [fuelItemsPerPage, setFuelItemsPerPage] = useState(10);
  const [fuelCurrentPage, setFuelCurrentPage] = useState(0);
  const [fuelTotalCount, setFuelTotalCount] = useState(0);

  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    if (deleteId !== null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);

  const updateStatus = async (id, status) => {
    try {
      const res = await updateStatusApi(id, { status: status });
      if (res?.success === true) {
        triggerSnackbar("Status updated successfully", true);
        fetchVehicle();
      } else {
        triggerSnackbar(res?.message || "Status update failed", false);
      }
    } catch {
      triggerSnackbar("Error updating status", false);
    }
  };

  const deleteProduct = async () => {
    try {
      const res = await deleteVehicleApi(deleteId);
      if (res?.data?.success === true) {
        triggerSnackbar("Vehicle deleted successfully", true);
        setIsOpenDelete(false);
        setTimeout(() => navigate(-1), 1500);
      } else {
        triggerSnackbar(res?.data?.message || "Failed to delete vehicle", false);
      }
    } catch {
      triggerSnackbar("Error deleting vehicle", false);
    }
  };

  const getKMEntry = async (page = kmCurrentPage, limit = kmItemsPerPage) => {
    try {
      setIsKMLoading(true);
      const params = {
        page: page + 1,
        limit: limit,
      };
      const res = await getKMEntryApi(id, params);
      if (res?.data?.success === true || res?.data?.status === true) {
        setLastKM(sanitizeInput(String(res.data.lastkm ?? "0")));
        const sanitizedKM = (res.data.km_entry ?? res.data.kmentry ?? []).map((r) => ({
          ...r,
          created_date: sanitizeInput(String(r.created_date || "")),
          last_km_record: sanitizeInput(String(r.last_km_record || "")),
          current_km: sanitizeInput(String(r.current_km || "")),
          file_path: sanitizeInput(String(r.file_path || "")),
          description: sanitizeInput(String(r.description || r.driver_name || "-")),
        }));
        setKilometerList(sanitizedKM);
        setKmTotalCount(res.data.totalcount ?? 0);
      }
    } catch {
      // Error handled by service
    } finally {
      setIsKMLoading(false);
    }
  };

  const getFuelEntry = async (page = fuelCurrentPage, limit = fuelItemsPerPage) => {
    try {
      setIsFuelLoading(true);
      const params = {
        page: page + 1,
        limit: limit,
      };
      const res = await getFuelEntryApi(id, params);
      if (res?.data?.success === true || res?.data?.status === true) {
        setLastKM(sanitizeInput(String(res.data.last_km ?? "0")));
        const sanitizedFuel = (res.data.fuel_entry ?? res.data.fuelentry ?? []).map((r) => ({
          ...r,
          created_date: sanitizeInput(String(r.created_date || "")),
          vendor_name: sanitizeInput(String(r.vendor_name || "")),
          current_km_fueling: sanitizeInput(String(r.current_km_fueling || "")),
          fuel_quentity: sanitizeInput(String(r.fuel_quentity || "")),
          fuel_price: sanitizeInput(String(r.fuel_price || "")),
          total_amount: sanitizeInput(String(r.total_amount || "")),
          file_path: sanitizeInput(String(r.file_path || "")),
        }));
        setFuelList(sanitizedFuel);
        setFuelTotalCount(res.data.totalcount ?? 0);
      }
    } catch {
      // Error handled by service
    } finally {
      setIsFuelLoading(false);
    }
  };

  // Pagination for KM - server-side, no slicing needed
  const kmPageCount = Math.ceil(kmTotalCount / kmItemsPerPage);

  const handleKmPageClick = (event) => {
    setKmCurrentPage(event.selected);
    getKMEntry(event.selected, kmItemsPerPage);
  };

  // Pagination for Fuel - server-side, no slicing needed
  const fuelPageCount = Math.ceil(fuelTotalCount / fuelItemsPerPage);

  const handleFuelPageClick = (event) => {
    setFuelCurrentPage(event.selected);
    getFuelEntry(event.selected, fuelItemsPerPage);
  };

  return (
    <>
      <DeleteModal
        title={"vehicle"}
        isOpen={isOpenDelete}
        onClose={() => {
          setIsOpenDelete(false);
          setDeleteId(null);
        }}
        onDelete={deleteProduct}
      />
      <div className="mt-2">
        <KMEntryModal
          id={id}
          lastKM={lastKM}
          show={showKMModal}
          onHide={() => setShowKMModal(false)}
          onSubmit={async (response) => {
            if (response.success === true) {
              triggerSnackbar(response.message || "KM entry saved", true);
              setShowKMModal(false);
              setKmCurrentPage(0);
              await getKMEntry(0, kmItemsPerPage);
              await fetchVehicle();
            } else {
              triggerSnackbar(response.message || "Failed to save km entry", false);
            }
          }}
        />
        <FuelEntryModal
          id={id}
          lastKM={lastKM}
          show={showFuelModal}
          onHide={() => setShowFuelModal(false)}
          onSubmit={async (response) => {
            if (response.success === true) {
              triggerSnackbar(response.message || "Fuel entry saved", true);
              setShowFuelModal(false);
              setFuelCurrentPage(0);
              await getFuelEntry(0, fuelItemsPerPage);
              await fetchVehicle();
            } else {
              triggerSnackbar(response.message || "Failed to save fuel entry", false);
            }
          }}
        />
        <div className="scroll-hidden" style={{ background: "#F5F7FA", height: "calc(100vh - 56px)" }}>
          {isLoading ? (
            // Skeleton Loading UI
            <>
              <div className="py-2 bg-white d-flex align-items-center justify-content-between" style={{ borderTopLeftRadius: 16, borderTopRightRadius: 16 }}>
                <div className="d-flex align-items-center">
                  <div className="ms-2">
                    <div className="skeleton skeleton-button" style={{ width: 36, height: 36, borderRadius: "100px" }}></div>
                  </div>

                  <div className="ms-3 d-flex align-items-center">
                    <div className="skeleton skeleton-avatar"></div>

                    <div className="ms-3 d-flex flex-column align-items-start gap-2">
                      <div className="skeleton skeleton-text-lg" style={{ width: 180 }}></div>
                      <div className="d-flex align-items-center gap-2">
                        <div className="skeleton skeleton-text-sm" style={{ width: 100 }}></div>
                        <div className="skeleton" style={{ width: 5, height: 5, borderRadius: "100px" }}></div>
                        <div className="skeleton skeleton-text-sm" style={{ width: 80 }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-end me-2 gap-2">
                  <div className="skeleton skeleton-dropdown"></div>
                  <div className="skeleton skeleton-button" style={{ width: 36, height: 36, borderRadius: "100px" }}></div>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12 col-lg-6">
                  <div className="bg-white rounded-4 p-3">
                    <div className="skeleton skeleton-text-lg mb-3" style={{ width: 150 }}></div>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="skeleton skeleton-detail-row"></div>
                    ))}
                  </div>
                </div>

                <div className="col-12 col-lg-6">
                  <div className="bg-white rounded-4 p-3">
                    <div className="skeleton skeleton-text-lg mb-3" style={{ width: 150 }}></div>
                    <div className="d-flex flex-wrap gap-2">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="skeleton skeleton-image"></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="m-2 bg-white rounded-4 p-3 mt-3">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex gap-2">
                    <div className="skeleton skeleton-tab"></div>
                    <div className="skeleton skeleton-tab"></div>
                  </div>
                  <div className="skeleton skeleton-button" style={{ width: 150 }}></div>
                </div>

                <div className="skeleton skeleton-table-header mb-2"></div>
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="skeleton skeleton-table-row mb-2"></div>
                ))}
              </div>
            </>
          ) : (
            // Actual Content
            <>
              <div className="p-2 bg-white d-flex align-items-center justify-content-between" style={{ borderTopLeftRadius: 16, borderTopRightRadius: 16 }}>
                <div className="d-flex align-items-center">
                  <div className="ms-2 d-flex align-items-center justify-content-center  rounded-5 bg-white" style={{ width: 36, height: 36, border: "1px solid #EBEBEB" }}>
                    <MoveLeft className="cursor-pointer" onClick={() => navigate(-1)} />
                  </div>

                  <div className="ms-3 d-flex align-items-center">
                    <div
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: 12,
                        background: "#DFE8FF",
                      }}
                      className="d-flex align-items-center justify-content-center"
                    >
                      <img src={vehicle.vehicle_profile ?? truckIcon} alt="vehicle" style={{ width: 56, height: 56 }} />
                    </div>

                    <div className="ms-3 d-flex flex-column align-items-start">
                      <div className="fs-20 fw-600 textFontColor">{vehicle.license_plate ?? "-"}</div>
                      <div className="d-flex align-items-center justify-content-start">
                        <span className="ms-2 fs-13 fw-400 " style={{ color: "#A2A7AE" }}>
                          {vehicle.vehicle_name ?? ""}
                        </span>
                        <div
                          className="mx-2"
                          style={{
                            width: "5px",
                            height: "5px",
                            borderRadius: "100px",
                            background: "#D9D9D9",
                          }}
                        ></div>
                        <div className="d-flex align-items-center justify-content-center rounded-4 px-2 py-1" style={{ background: "#EDF2FF", color: "#3469E7" }}>
                          <span className="fs-12 fw-500">{vehicle.type_label ?? ""}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-end me-2  gap-2">
                  {checkPermission("edit_permission") && (
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="light"
                        id="dropdown-sort"
                        className={`no-caret d-flex align-items-center justify-content-between rounded-5 ${vehicle.status === true ? "backgroundGreenLt" : "backgroundLightOrange"}`}
                        style={{
                          padding: "6px 12px",
                          border: `1px solid ${vehicle.status === true ? "#00A438" : "#FFC107"}`,
                        }}
                      >
                        <span className="d-flex align-items-center px-1 gap-2">
                          <span
                            className={vehicle.status === true ? "backgroundGreen" : "backgroundYellow"}
                            style={{
                              width: "10px",
                              height: "10px",
                              borderRadius: "3px",
                              display: "inline-block",
                            }}
                          ></span>

                          <span className="dropdown-text textcolor fs-13 fw-500">{vehicle.status === true ? "Active" : "In-Active"}</span>

                          <span className="dropdown-toggle-arrow">
                            <DownArrowIc />
                          </span>
                        </span>
                      </Dropdown.Toggle>

                      <Dropdown.Menu
                        style={{
                          backgroundColor: "white",
                          borderRadius: "8px",
                          border: "1px solid #EBEBEB",
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        {statusListOption.map((option) => (
                          <Dropdown.Item
                            className="fs-14 fw-500 textcolor"
                            key={option.value}
                            onClick={() => updateStatus(id, option.value)}
                          // active={selected === option}
                          >
                            {option.label}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}

                  <div onClick={(e) => e.stopPropagation()}>
                    {(checkPermission("edit_permission") || checkPermission("delete_permission")) && (
                      <Dropdown>
                        <Dropdown.Toggle
                          variant="light"
                          id="dropdown-sort"
                          className="d-flex align-items-center justify-content-between rounded-5 bg-white dropdown-toggle-no-caret"
                          style={{
                            width: "36px",
                            height: "36px",
                            border: "1px solid #EBEBEB",
                          }}
                        >
                          <span className="d-flex align-items-center px-1 gap-2">
                            <ThreeDotIC />
                          </span>
                        </Dropdown.Toggle>

                        <Dropdown.Menu
                          style={{
                            backgroundColor: "white",
                            borderRadius: "8px",
                            border: "1px solid #EBEBEB",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          {checkPermission("edit_permission") && (
                            <Dropdown.Item
                              className="fs-14 fw-500 textcolor"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate("/vehicle/create", {
                                  state: { id },
                                });
                              }}
                            >
                              Edit
                            </Dropdown.Item>
                          )}

                          {checkPermission("delete_permission") && (
                            <Dropdown.Item
                              className="fs-14 fw-500 textcolor"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteId(id);
                              }}
                            >
                              Delete
                            </Dropdown.Item>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </div>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12 col-lg-6">
                  <div className="bg-white rounded-4 p-3">
                    <h6 className="fs-16 fw-600 text-start ps-2">Vehicle details</h6>
                    <div className="mt-3 d-flex align-items-center text-start ps-2">
                      <div style={{ width: 160 }} className="cardTextGrey fs-13 fw-500">
                        VIN
                      </div>
                      <div className="fs-14 fw-500 textFontColor">{vehicle.vin ?? "-"}</div>
                    </div>
                    <div className="mt-3 d-flex align-items-center text-start ps-2">
                      <div style={{ width: 160 }} className="cardTextGrey fs-13 fw-500">
                        Group
                      </div>
                      <div className="fs-14 fw-500 textFontColor">{vehicle.group_location ?? "-"}</div>
                    </div>
                    <div className="mt-3 d-flex align-items-center text-start ps-2">
                      <div style={{ width: 160 }} className="cardTextGrey fs-13 fw-500">
                        Vehicle KM
                      </div>
                      <div className="fs-14 fw-500 textFontColor">{vehicle.vehicle_km ?? "-"}</div>
                    </div>
                    <div className="mt-3 d-flex align-items-center text-start ps-2">
                      <div style={{ width: 160 }} className="cardTextGrey fs-13 fw-500">
                        Color
                      </div>
                      <div className="fs-14 fw-500 textFontColor">{vehicle.vehicle_color ?? "-"}</div>
                    </div>
                    <div className="mt-3 d-flex align-items-center text-start ps-2">
                      <div style={{ width: 160 }} className="cardTextGrey fs-13 fw-500">
                        Body Type
                      </div>
                      <div className="fs-14 fw-500 textFontColor">{vehicle.body_type ?? "-"}</div>
                    </div>
                    <div className="mt-3 d-flex align-items-center text-start ps-2">
                      <div style={{ width: 160 }} className="cardTextGrey fs-13 fw-500">
                        Dimension ({vehicle.dimension ?? ""})
                      </div>
                      <div className="fs-14 fw-500 textFontColor">
                        L = {vehicle.length ?? "-"} | B = {vehicle.width ?? "-"} | H = {vehicle.height ?? "-"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-lg-6">
                  <div className="bg-white rounded-4 p-3">
                    <h6 className="fs-16 fw-600">Vehicle Images</h6>
                    <div className="mt-3 d-flex flex-wrap gap-2">
                      {(vehicle.Attachments || []).map((img, i) => (
                        <div
                          key={i}
                          style={{
                            width: 80,
                            height: 80,
                          }}
                        >
                          <img src={img.file_path} className="w-100 h-100 object-fit-cover rounded-2 cursor-pointer p-1 border-0" style={{ background: "#F5F5F5" }} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              {/* Km & Fuel Entry */}
              <div className="m-0 bg-white rounded-4 p-3 mt-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div>
                    <div
                      className={`d-inline-block rounded-3 px-3 py-2 me-2 fs-13  ${selectedTab === "kilometer" ? " fw-500 " : " fw-400"}`}
                      style={{
                        cursor: "pointer",
                        borderRadius: "36px",
                        color: selectedTab === "kilometer" ? "#3469E7" : "#5F5E5B",
                        background: selectedTab === "kilometer" ? "#E6EDFF" : "#F5F7FB",
                      }}
                      onClick={() => setSelectedTab("kilometer")}
                    >
                      Kilometer Entry
                    </div>
                    <div
                      className={`d-inline-block rounded-3 px-3 py-2 fs-13  ${selectedTab === "fuel" ? "fw-500 " : "fw-400"}`}
                      style={{
                        cursor: "pointer",
                        borderRadius: "36px",
                        color: selectedTab === "fuel" ? "#3469E7" : "#5F5E5B",
                        background: selectedTab === "fuel" ? "#E6EDFF" : "#F5F7FB",
                      }}
                      onClick={() => setSelectedTab("fuel")}
                    >
                      Fuel Entry
                    </div>
                  </div>

                  <div className="d-flex gap-2">
                    {/* <Button className="btn-border-grey">Download Report</Button> */}
                    {selectedTab === "fuel" && (
                      <Button className="btn-primary" onClick={() => setShowFuelModal(true)}>
                        + Add Fuel
                      </Button>
                    )}
                    {selectedTab === "kilometer" && (
                      <Button className="btn-primary" onClick={() => setShowKMModal(true)}>
                        + Add Kilometer
                      </Button>
                    )}
                  </div>
                </div>

                {selectedTab === "kilometer" && (
                  <>
                    {isKMLoading ? (
                      <div>
                        <div className="skeleton skeleton-table-header mb-2"></div>
                        {[1, 2].map((i) => (
                          <div key={i} className="skeleton skeleton-table-row mb-2"></div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ overflowX: "auto" }}>
                        <table
                          className="stop-table"
                          style={{
                            width: "100%",
                            border: "1px solid #EBEBEB",
                            borderCollapse: "collapse",
                          }}
                        >
                          <thead
                            style={{
                              height: "48px",
                              color: "#8790A1",
                              fontSize: "11px",
                              fontWeight: "400",
                              textAlign: "left",
                            }}
                          >
                            <tr>
                              <th className="ps-2">DATE</th>
                              <th className="ps-2">LAST KM</th>
                              <th className="ps-2">CURRENT KM</th>
                              <th className="ps-2">ATTACHMENT</th>
                              <th className="ps-2">DESCRIPTION</th>
                            </tr>
                          </thead>
                          <tbody>
                            {kilometerList.map((r, idx) => (
                              <tr key={idx} style={{ height: "32px" }}>
                                <td className="ps-2 fs-14 fw-400 textFontColor">{r.created_date}</td>
                                <td className="ps-2 fs-14 fw-400 textFontColor">{r.last_km_record}</td>
                                <td className="ps-2 fs-14 fw-400 textFontColor">{r.current_km}</td>
                                <td className="ps-2 fs-14 fw-400 textFontColor">
                                  {r.file_path ? (
                                    <a href={r.file_path} target="_blank" rel="noreferrer">
                                      View
                                    </a>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td className="ps-2 fs-14 fw-400 textFontColor">{r.description || r.driver_name || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
                {selectedTab === "kilometer" && (
                  <CommonPagination
                    itemsPerPage={kmItemsPerPage}
                    setItemsPerPage={(newLimit) => {
                      setKmItemsPerPage(newLimit);
                      setKmCurrentPage(0);
                      getKMEntry(0, newLimit);
                    }}
                    pageCount={kmPageCount}
                    onPageChange={handleKmPageClick}
                    forcePage={kmCurrentPage}
                  />
                )}
                {selectedTab === "fuel" && (
                  <>
                    {isFuelLoading ? (
                      <div>
                        <div className="skeleton skeleton-table-header mb-2"></div>
                        {[1, 2].map((i) => (
                          <div key={i} className="skeleton skeleton-table-row mb-2"></div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ overflowX: "auto" }}>
                        <table
                          className="stop-table"
                          style={{
                            width: "100%",
                            border: "1px solid #EBEBEB",
                            borderCollapse: "collapse",
                          }}
                        >
                          <thead
                            style={{
                              height: "48px",
                              color: "#8790A1",
                              fontSize: "11px",
                              fontWeight: "400",
                              textAlign: "left",
                            }}
                          >
                            <tr>
                              <th className="ps-2">DATE</th>
                              <th className="ps-2">VENDOR</th>
                              <th className="ps-2">KILOMETER</th>
                              <th className="ps-2">FUEL QTY</th>
                              <th className="ps-2">FUEL PRICE</th>
                              <th className="ps-2">TOTAL AMOUNT</th>
                              <th className="ps-2">ATTACHMENT</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fuelList.map((r, idx) => (
                              <tr key={idx} style={{ height: "32px" }}>
                                <td className="ps-2 fs-14 fw-400 textFontColor">{r.created_date}</td>
                                <td className="ps-2 fs-14 fw-400 textFontColor">{r.vendor_name}</td>
                                <td className="ps-2 fs-14 fw-400 textFontColor">{r.current_km_fueling}</td>
                                <td className="ps-2 fs-14 fw-400 textFontColor">{r.fuel_quentity}</td>
                                <td className="ps-2 fs-14 fw-400 textFontColor">{r.fuel_price}</td>
                                <td className="ps-2 fs-14 fw-400 textFontColor">{r.total_amount}</td>
                                <td className="ps-2 fs-14 fw-400 textFontColor">
                                  {r.file_path ? (
                                    <a href={r.file_path} target="_blank" rel="noreferrer">
                                      View
                                    </a>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
                {selectedTab === "fuel" && (
                  <CommonPagination
                    itemsPerPage={fuelItemsPerPage}
                    setItemsPerPage={(newLimit) => {
                      setFuelItemsPerPage(newLimit);
                      setFuelCurrentPage(0);
                      getFuelEntry(0, newLimit);
                    }}
                    pageCount={fuelPageCount}
                    onPageChange={handleFuelPageClick}
                    forcePage={fuelCurrentPage}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <CommonSnackbar show={snackbar.show} message={snackbar.message} isSuccess={snackbar.status} onClose={handleSnackbarClose} />
    </>
  );
};
